import type { ReactNode } from "react";
import { SafeExternalLink } from "@/components/SafeExternalLink";
import { SaveButton } from "@/components/SaveButton";
import { EditorialImage } from "@/components/EditorialImage";
import { OutboundArrow } from "@/components/OutboundArrow";
import { getMotifFallback } from "@/lib/motif-fallback";
import { normalizeTitle } from "@/lib/normalize-title";

/**
 * List-row-kort til undersider (Artikler, Emne, Bogmærker).
 */

type Props = {
  id: string;
  kicker: ReactNode;
  title: string;
  href: string;
  imageUrl: string | null;
  topic?: string | null;
  reason?: ReactNode;
  sourceMeta?: ReactNode;
  outLabel?: string;
  onOpen?: () => void;
  variant?: "row" | "lead";
  unread?: boolean;
};

export function ArticleListRow({
  id,
  kicker,
  title,
  href,
  imageUrl,
  topic,
  reason,
  sourceMeta,
  outLabel = "Læs",
  onOpen,
  variant = "row",
  unread = true,
}: Props) {
  const isLead = variant === "lead";

  return (
    <article
      className={`grid gap-6 md:gap-10 py-6 md:py-8 border-b border-rule ${
        isLead
          ? "md:grid-cols-[1.3fr_1fr] items-center"
          : "md:grid-cols-[240px_1fr]"
      }`}
    >
      <SafeExternalLink
        href={href}
        onOpen={onOpen}
        className="block group focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-4"
        aria-label={`Læs "${title}" hos kilden`}
      >
        <EditorialImage
          src={imageUrl}
          alt={title}
          ratio={isLead ? "3/2" : "3/2"}
          fallback={getMotifFallback("article", topic)}
        />
      </SafeExternalLink>


      <div className="flex flex-col min-w-0">
        <p className="eyebrow mb-2.5 min-w-0 [overflow-wrap:anywhere]">{kicker}</p>

        <h3
          className={`font-serif font-normal m-0 mb-3 leading-[1.15] text-ink [overflow-wrap:anywhere] ${
            isLead
              ? "text-[28px] md:text-[34px] leading-[1.08]"
              : "text-[22px] md:text-[26px]"
          } ${unread ? "" : "text-ink-soft"}`}
        >
          <SafeExternalLink
            href={href}
            onOpen={onOpen}
            className="hover:text-accent transition-colors"
          >
            {normalizeTitle(title)}
          </SafeExternalLink>
        </h3>


        {reason && (
          <>
            <p className="eyebrow mb-1.5">Derfor valgt</p>
            <p className="font-serif font-light text-[15px] md:text-[16px] leading-[1.65] text-ink-body m-0 mb-4 max-w-[52ch] [overflow-wrap:anywhere]">
              {reason}
            </p>
          </>
        )}

        <div className="mt-auto grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-3 sm:flex sm:flex-wrap sm:justify-between">
          {sourceMeta && (
            <span className="min-w-0 max-w-full font-mono text-[10.5px] tracking-[0.12em] uppercase text-muted [overflow-wrap:anywhere]">
              {sourceMeta}
            </span>
          )}
          <div className="flex min-w-0 shrink-0 items-center gap-5">
            <SaveButton id={id} />
            <SafeExternalLink
              href={href}
              onOpen={onOpen}
              className="min-w-0 font-serif italic text-[14px] md:text-[15px] text-accent hover:text-accent-dark underline underline-offset-4 [overflow-wrap:anywhere]"
            >
              <span>{outLabel}<OutboundArrow /></span>
            </SafeExternalLink>
          </div>
        </div>
      </div>
    </article>
  );
}
